import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronDown, Clock, LoaderCircle, ScanText } from 'lucide-react';
import {
  REASON_LABELS,
  detectLongSentence,
  lemmaOf,
  type SentenceAnalysisResult,
  type Token,
} from '@en3/shared';
import { contentApi } from '@/api/content.api';
import { analysisApi } from '@/api/analysis.api';
import { queryKeys } from '@/api/client';
import { isApiError, toDisplayMessage } from '@/api/errors';
import { track } from '@/api/events.api';
import { ROUTES } from '@/app/routes';
import { useReaderSettings } from '@/stores/reader.store';
import { AnalysisCard } from '@/components/analysis/AnalysisCard';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '@/components/common/States';
import { Badge } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { ProgressBar } from '@/components/reader/ProgressBar';
import { ReaderToolbar } from '@/components/reader/ReaderToolbar';
import { SentenceText } from '@/components/reader/SentenceText';
import { WordCard, type WordCardTarget } from '@/components/reader/WordCard';
import { cn } from '@/lib/cn';

/**
 * 文章阅读器（Kindle 式）。
 *
 * 页面的核心复杂度不在"渲染正文"，而在三件**容易被做错**的事，这里都单独写清楚：
 *
 * 1. **点词查义不打断阅读**：词卡是底部抽屉，Esc/点遮罩关闭，查完自动回到原处
 *    （`Dialog` 会把焦点还给触发它的那个词）。缓存键带上下文，见 `WordCard`。
 * 2. **进度上报的策略**（见 `flushProgress`）：只在"页面可见"时计时，每 30 秒或
 *    跨过 20 句打一次增量；离开页面再补一次。失败一律静默且**不重发** ——
 *    网络抖一下就在每个 tick 重发，会把一次阅读变成几百个请求。
 * 3. **正文渲染的性能**：句子渲染交给 `React.memo` 的 `SentenceText`，
 *    本页传给它的回调全部用 `useCallback` 固定引用。因此滚动、查词、解析、
 *    调字号都不会让整篇正文（可能两三千个 span）重渲染。
 */
export function ArticleReaderPage() {
  const { articleId = '' } = useParams<{ articleId: string }>();

  // 只订阅真正影响正文渲染的两个设置：订阅整个 store 会让调字号时的重渲染面积无谓变大
  const highlightLong = useReaderSettings((s) => s.highlightLong);
  const autoCollect = useReaderSettings((s) => s.autoCollect);

  const [target, setTarget] = useState<WordCardTarget | null>(null);
  const [panel, setPanel] = useState<{ sentenceId: string; globalIndex: number; result: SentenceAnalysisResult | null } | null>(
    null,
  );
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  /**
   * 已经忽略过「继续上次阅读」提示的文章 id。
   *
   * 只记 id 而不是布尔值：切到另一篇文章时提示应当重新出现，
   * 用 id 比较既表达了"这篇已经问过了"，也天然支持多篇文章来回切换。
   */
  const [resumeDismissedFor, setResumeDismissedFor] = useState<string | null>(null);
  const [showLongList, setShowLongList] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);
  const [localFinished, setLocalFinished] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  /** 用于进度条显示的句子序号。刻意与 `lastIndexRef` 分开：见下面的滚动节流 */
  const [displayIndex, setDisplayIndex] = useState(0);

  const readSecondsRef = useRef(0);
  const lastIndexRef = useRef(0);
  const reportedSecondsRef = useRef(0);
  const reportedIndexRef = useRef(0);
  const proseRef = useRef<HTMLElement | null>(null);
  const initedArticleRef = useRef<string | null>(null);
  const trackedArticleRef = useRef<string | null>(null);
  const lastAnalyzeRef = useRef<{ sentenceId: string; globalIndex: number } | null>(null);
  /** 用户是否真的滚动过。没滚过就不认 IO 观测到的位置（见 flushProgress 里的说明） */
  const hasScrolledRef = useRef(false);

  const articleQuery = useQuery({
    queryKey: queryKeys.article(articleId),
    queryFn: () => contentApi.article(articleId),
    // 登录态相关的一致性由 api/client 统一保证（所有请求都会先等 bootstrap 完成），
    // 这里只判断"有没有 articleId"
    enabled: articleId.length > 0,
  });
  const article = articleQuery.data?.article;

  /** 扁平的句子列表：进度计算、长难句清单回跳都要用它 */
  const sentences = useMemo(() => (article?.paragraphs ?? []).flatMap((paragraph) => paragraph.sentences), [article]);
  const totalSentences = sentences.length || article?.sentenceCount || 0;

  const longQuery = useQuery({
    queryKey: queryKeys.articleLongSentences(articleId),
    queryFn: () => contentApi.articleLongSentences(articleId),
    // 懒加载：一篇文章可能有几十个长难句，默认折叠就不该发这个请求
    enabled: showLongList && articleId.length > 0,
  });

  /**
   * 长难句清单里的"难度 + 判难依据"用 shared 的规则函数现算。
   *
   * 为什么不另开接口：`/articles/:id/long-sentences` 返回的 `ArticleSentenceView` 里
   * 没有 reasons，而判定逻辑本来就在 `shared/lib/longSentence`（后端入库用的是同一份）。
   * 前端复算不会与后端不一致，也省掉一次进正文前的等待。
   */
  const longItems = useMemo(() => {
    return (longQuery.data?.sentences ?? []).map((sentence) => {
      const verdict = detectLongSentence(sentence.text);
      return { sentence, score: sentence.difficultyScore ?? verdict.score, reasons: verdict.reasons };
    });
  }, [longQuery.data]);

  const scrollToSentence = useCallback((globalIndex: number) => {
    const node = document.getElementById(`s-${globalIndex}`);
    node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  /**
   * 进度上报。
   *
   * 策略与取舍：
   * - **增量语义**：`readSecondsDelta` 是"自上次上报以来新增的秒数"，用绝对计数相减得到，
   *   所以天然不会重复计数；`visibilitychange` 时清零不需要额外代码。
   * - **先记后发**：发出前就把 `reported*` 指针推到当前值。失败时不回退、不重试 ——
   *   否则一次断网会让每个 tick 都重发一次，把阅读变成请求风暴。代价是"少记一点进度"，
   *   这比打断阅读轻得多。
   * - **空转保护**：秒数与位置都没变就根本不发请求（首次进入、StrictMode 下的假卸载都会命中）。
   */
  const flushProgress = useCallback(
    async (extra?: { finished?: boolean; selfRating?: number }) => {
      if (articleId.length === 0) return;
      const totalSeconds = readSecondsRef.current;
      const deltaSeconds = totalSeconds - reportedSecondsRef.current;
      /**
       * 关键取舍：**用户还没滚动过时，位置沿用"已知值"而不是 IO 观测到的值**。
       *
       * 从目录点进一篇读到第 301 句的文章时，浏览器停在页面顶部，IO 立刻会把当前位置
       * 记为第 1–3 句。如果照发，用户只是"打开看一眼再返回"就会把已保存的进度冲回开头 ——
       * 这是最难被发现、也最伤用户的一类 bug。滚动是"真的在读"的可靠信号，
       * 因此第一次滚动之前不上报新位置（时间照常累计）。
       */
      const lastGlobalIndex = hasScrolledRef.current ? lastIndexRef.current : reportedIndexRef.current;
      const moved = lastGlobalIndex !== reportedIndexRef.current;

      if (!extra?.finished && !moved && deltaSeconds <= 0) return;

      reportedSecondsRef.current = totalSeconds;
      reportedIndexRef.current = lastGlobalIndex;

      try {
        await contentApi.reportProgress(articleId, {
          lastGlobalIndex,
          ...(deltaSeconds > 0 ? { readSecondsDelta: deltaSeconds } : {}),
          ...(extra?.finished ? { finished: true } : {}),
          ...(extra?.selfRating === undefined ? {} : { selfRating: extra.selfRating }),
        });
      } catch {
        // 静默：进度上报失败不能打断阅读，也不提示（用户无法对此做任何事）
      }
    },
    [articleId],
  );

  /** 打开文章埋点：同一次挂载只报一次（StrictMode 会跑两遍 effect） */
  useEffect(() => {
    if (articleId.length === 0 || trackedArticleRef.current === articleId) return;
    trackedArticleRef.current = articleId;
    track({ type: 'read', articleId });
  }, [articleId]);

  /**
   * 首次拿到文章时对齐位置。
   *
   * 用 ref 守住"每篇文章只初始化一次"：`reportProgress` 之后若文章查询被刷新，
   * 服务端的 `lastGlobalIndex` 会变，但那不应该把用户已经读到的位置拽回去。
   */
  useEffect(() => {
    if (!article || initedArticleRef.current === article.id) return;
    initedArticleRef.current = article.id;
    const start = article.progress?.lastGlobalIndex ?? 0;
    lastIndexRef.current = start;
    // 已上报位置也从这里起步：否则刚进页面就会因为"位置从 0 变成 N"多发一次请求
    reportedIndexRef.current = start;
    setDisplayIndex(start);
  }, [article]);

  /**
   * 「继续上次阅读」提示条：**从文章数据派生，而不是用 effect 写进 state**。
   *
   * 条件就是"服务端有进度 + 这篇文章还没被用户忽略过"，天然只在进入时出现一次；
   * 用户点过"知道了"或跳过去之后，把文章 id 记进 `resumeDismissedFor`，
   * 该提示对这篇文章就再也不会出现（切到别的文章仍然会提示）。
   * 这样既没有 effect，也不会出现"上报进度后 lastGlobalIndex 变了、提示条又冒出来"的抖动。
   */
  const resumeAt =
    article && resumeDismissedFor !== article.id ? (article.progress?.lastGlobalIndex ?? 0) : 0;
  const showResume = resumeAt > 0;

  /** 阅读计时：每 1 秒 +1，只在页面可见时累加（切走标签页不算阅读） */
  useEffect(() => {
    if (articleId.length === 0) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      readSecondsRef.current += 1;
      const secondsSinceReport = readSecondsRef.current - reportedSecondsRef.current;
      const sentencesSinceReport = Math.abs(lastIndexRef.current - reportedIndexRef.current);
      if (secondsSinceReport >= REPORT_INTERVAL_SECONDS || sentencesSinceReport >= REPORT_SENTENCE_DELTA) {
        void flushProgress();
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [articleId, flushProgress]);

  /** 离开页面（切标签页/关页/路由跳走）再补一次上报 */
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') void flushProgress();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    // pagehide 比 unload 可靠（移动端 Safari 常常不发 unload），只做"尽力而为"
    window.addEventListener('pagehide', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onVisibilityChange);
      void flushProgress();
    };
  }, [flushProgress]);

  /**
   * 用 IntersectionObserver 判断"读到哪一句"。
   *
   * 不用 `scroll` + `getBoundingClientRect` 逐句测量：正文有几百个句子节点，
   * 每次滚动都读取布局会强制同步重排（forced reflow），是滚动卡顿的经典来源。
   * 这里只在句子进出"阅读带"（上 15% 到下 65% 之间的那条带）时回调一次，
   * 取带内最靠上的一句作为当前位置 —— 视线通常停在那里。
   */
  useEffect(() => {
    const container = proseRef.current;
    if (!container || sentences.length === 0) return;

    const nodes = Array.from(container.querySelectorAll<HTMLElement>('[data-global-index]'));
    if (nodes.length === 0) return;

    const visible = new Set<number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = Number((entry.target as HTMLElement).dataset.globalIndex);
          if (!Number.isFinite(index)) continue;
          if (entry.isIntersecting) visible.add(index);
          else visible.delete(index);
        }
        if (visible.size === 0) return;
        lastIndexRef.current = Math.min(...visible);
      },
      { rootMargin: '-15% 0px -65% 0px', threshold: 0 },
    );

    for (const node of nodes) observer.observe(node);
    return () => observer.disconnect();
  }, [sentences.length, article?.id]);

  /**
   * 把 ref 里的位置"节流"同步到 state，供进度条显示。
   *
   * 为什么不直接把 IO 的结果写进 state：IO 一次滚动可能回调好几次，每次都重渲染
   * 会把页面拖慢；而进度条只需要"半秒内够新"。用 rAF 合并同一帧内的多次滚动事件，
   * 再用 500ms 节流限制 setState 频率 —— 正文一个字节都不会因此重渲染
   * （`SentenceText` 的 props 没有任何一项随滚动变化）。
   */
  useEffect(() => {
    let lastSync = 0;
    let frame = 0;
    const onScroll = () => {
      // 第一次滚动就认为"用户开始读了"，之后上报才认 IO 观测到的位置
      hasScrolledRef.current = true;
      if (frame !== 0) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const now = Date.now();
        if (now - lastSync < DISPLAY_SYNC_THROTTLE_MS) return;
        lastSync = now;
        const next = lastIndexRef.current;
        setDisplayIndex((previous) => (previous === next ? previous : next));
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, []);

  const { mutateAsync: analyzeAsync } = useMutation({
    mutationFn: (sentenceId: string) => analysisApi.analyze({ sentenceId }),
  });

  /**
   * 把 `mutateAsync` 关进 ref 后再用。
   *
   * v5 的 `mutateAsync` 取的是 store 快照（`result.mutate`），mutation 状态一变引用就变。
   * 若直接写进 `runAnalyze` 的依赖，解析期间 `runAnalyze → handleAnalyzeClick` 会换两次引用，
   * 把 `SentenceText` 的 memo 击穿、整篇正文白重渲染一次。所有版本的 `mutateAsync` 都绑在
   * 同一个 observer 上，用 ref 转发行为完全等价，但回调引用恒定。
   */
  const analyzeRef = useRef(analyzeAsync);
  useEffect(() => {
    analyzeRef.current = analyzeAsync;
  }, [analyzeAsync]);

  /**
   * 触发长难句解析。
   *
   * 点击时先滚到目标句：面板在正文**上方**展开，用户需要先确认"解析的是这一句"。
   * 结果到达后不再自动滚动 —— 面板撑开会把句子顶下去，视线的落点正好是解析内容，
   * 再滚一次反而会让人觉得页面在乱跳。面板头部另有「回到本句」。
   */
  const runAnalyze = useCallback(
    async (sentenceId: string, globalIndex: number) => {
      lastAnalyzeRef.current = { sentenceId, globalIndex };
      setAnalysisError(null);
      setPanel({ sentenceId, globalIndex, result: null });
      window.requestAnimationFrame(() => scrollToSentence(globalIndex));

      try {
        const result = await analyzeRef.current(sentenceId);
        // 竞态保护：解析期间用户点了别的句子，这次结果就丢掉，不要覆盖新面板
        if (lastAnalyzeRef.current?.sentenceId !== sentenceId) return;
        setPanel({ sentenceId, globalIndex, result });
      } catch (error) {
        if (lastAnalyzeRef.current?.sentenceId !== sentenceId) return;
        setAnalysisError(toDisplayMessage(error));
      }
    },
    [scrollToSentence],
  );

  const handleWordClick = useCallback((sentenceId: string, sentenceText: string, token: Token) => {
    setTarget({ surface: token.surface, lemma: token.lemma, sentenceText, sentenceId, tokenIndex: token.index });
  }, []);

  const handleAnalyzeClick = useCallback(
    (sentenceId: string, globalIndex: number) => {
      void runAnalyze(sentenceId, globalIndex);
    },
    [runAnalyze],
  );

  /** 解析卡里点关键词：没有 token 位置，但上下文用被解析的那句，文中义仍然算得出来 */
  const handleAnalysisWordClick = useCallback(
    (word: string) => {
      setTarget({
        surface: word,
        lemma: lemmaOf(word),
        sentenceText: panel?.result?.analysis.text ?? '',
        sentenceId: panel?.sentenceId,
      });
    },
    [panel],
  );

  const finishMutation = useMutation({
    mutationFn: (selfRating: number) => {
      // 读完这一下顺便把没上报的秒数一起发出去，少一次请求
      const deltaSeconds = readSecondsRef.current - reportedSecondsRef.current;
      // 与 flushProgress 同一条规则：没滚动过就沿用已知位置，别把进度冲回开头
      const lastGlobalIndex = hasScrolledRef.current ? lastIndexRef.current : reportedIndexRef.current;
      reportedSecondsRef.current = readSecondsRef.current;
      reportedIndexRef.current = lastGlobalIndex;
      return contentApi.reportProgress(articleId, {
        finished: true,
        selfRating,
        lastGlobalIndex,
        ...(deltaSeconds > 0 ? { readSecondsDelta: deltaSeconds } : {}),
      });
    },
    onSuccess: () => {
      setLocalFinished(true);
      setFinishOpen(false);
      setNotice('已标记读完。之后可以在「水平报告」里看到这篇的自评与阅读时长。');
    },
  });

  // -------------------------------------------------------------------------
  // 三态：加载 / 失败（含 404 与"没有 id"）/ 正常
  // -------------------------------------------------------------------------

  if (articleId.length === 0) {
    return (
      <ReaderFrame>
        <ErrorBlock message="链接里没有文章 id，无法打开。" />
        <BackToCatalog className="mt-4" />
      </ReaderFrame>
    );
  }

  if (articleQuery.isPending) {
    return (
      <ReaderFrame>
        <LoadingBlock label="正在加载文章…" />
      </ReaderFrame>
    );
  }

  if (articleQuery.isError || !article) {
    // 404 与"无权访问"在后端都是 NOT_FOUND（避免用 403 探测私有文章是否存在），
    // 所以文案必须把两种可能都说出来。
    const notFound = isApiError(articleQuery.error) && articleQuery.error.code === 'NOT_FOUND';
    if (notFound) {
      return (
        <ReaderFrame>
          <EmptyBlock
            title="文章不存在或没有访问权限"
            description="它可能已被删除、设为私有，或链接不完整。"
            action={<BackToCatalog />}
          />
        </ReaderFrame>
      );
    }
    return (
      <ReaderFrame>
        <ErrorBlock message={toDisplayMessage(articleQuery.error)} onRetry={() => void articleQuery.refetch()} />
        <BackToCatalog className="mt-4" />
      </ReaderFrame>
    );
  }

  const finished = localFinished || Boolean(article.progress?.finishedAt);
  const pct =
    totalSentences > 0 ? ((displayIndex + 1) / totalSentences) * 100 : (article.progress?.progressPct ?? 0);
  const analyzingId = panel && panel.result === null ? panel.sentenceId : null;
  const activeSentenceId = target?.sentenceId ?? panel?.sentenceId ?? null;

  return (
    <ReaderFrame>
      {/* ① 文章信息：标题 / 作者来源 / 统计 / 面包屑 */}
      <header>
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <Link to={ROUTES.textbooks} className="hover:text-foreground">
            教材目录
          </Link>
          {article.textbook && (
            <>
              <span aria-hidden>/</span>
              <Link to={ROUTES.textbooks} className="hover:text-foreground">
                {article.textbook.titleZh ?? article.textbook.title}
              </Link>
            </>
          )}
          {article.unit && (
            <>
              <span aria-hidden>/</span>
              <Link to={ROUTES.textbooks} className="hover:text-foreground">
                第 {article.unit.unitNo} 单元 {article.unit.titleZh ?? article.unit.title}
              </Link>
            </>
          )}
        </nav>

        <h2 className="mt-1.5 text-2xl leading-snug font-bold sm:text-3xl">{article.titleZh ?? article.title}</h2>
        {article.titleZh && article.titleZh !== article.title && (
          <p className="mt-1 font-serif text-sm text-muted-foreground">{article.title}</p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
          {(article.author || article.source) && <span>{article.author ?? article.source}</span>}
          <span>{article.wordCount} 词</span>
          <span>{article.sentenceCount} 句</span>
          {typeof article.difficultyScore === 'number' && <span>难度 {Math.round(article.difficultyScore)}/100</span>}
          {article.cefrEstimate && <Badge tone="primary">CEFR {article.cefrEstimate}</Badge>}
          {article.visibility === 'private' && <Badge tone="warning">仅你可见</Badge>}
          {article.progress && article.progress.readSeconds > 0 && (
            <span className="inline-flex items-center gap-1">
              <Clock aria-hidden className="size-3" />
              已读 {Math.round(article.progress.readSeconds / 60)} 分钟
            </span>
          )}
        </div>
      </header>

      {/* ② 工具栏 + 进度条：给一点粘性，滚动中也能调字号、看进度 */}
      <div className="sticky top-14 z-30 mt-4 space-y-2 bg-background/90 py-2 backdrop-blur">
        <ReaderToolbar finished={finished} onFinish={() => setFinishOpen(true)} />
        <ProgressBar pct={pct} currentIndex={displayIndex} totalSentences={totalSentences} finished={finished} />
      </div>

      {notice && (
        <p role="status" className="mt-3 rounded-md border bg-muted/40 px-3 py-2 text-sm">
          {notice}
        </p>
      )}

      {/* ③ 继续上次阅读 */}
      {showResume && article && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border bg-accent/40 px-3 py-2 text-sm">
          <span>
            继续上次阅读（第 {resumeAt + 1} 句）？
          </span>
          <span className="ml-auto flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                scrollToSentence(resumeAt);
                setResumeDismissedFor(article.id);
              }}
            >
              跳过去
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setResumeDismissedFor(article.id)}
            >
              不用了
            </Button>
          </span>
        </div>
      )}

      {/* ④ 长难句解析面板：占满内容宽度（不受正文行宽限制），可关闭 */}
      {panel && (
        <section className="mt-3" aria-live="polite">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold">长难句解析</h2>
            <div className="ml-auto flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => scrollToSentence(panel.globalIndex)}
              >
                回到本句
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setPanel(null)}>
                关闭
              </Button>
            </div>
          </div>
          {panel.result ? (
            <AnalysisCard result={panel.result} onWordClick={handleAnalysisWordClick} />
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-dashed px-3 py-4 text-sm text-muted-foreground">
              <LoaderCircle aria-hidden className="size-4 animate-spin" />
              正在解析这句话…（模型不可用时会自动降级为基础解析）
            </div>
          )}
        </section>
      )}

      {analysisError && (
        <ErrorBlock
          className="mt-3"
          message={analysisError}
          onRetry={() => {
            const last = lastAnalyzeRef.current;
            if (last) void runAnalyze(last.sentenceId, last.globalIndex);
          }}
        />
      )}

      {/* ⑤ 正文 */}
      {sentences.length === 0 ? (
        <EmptyBlock
          className="mt-6"
          title="这篇还没有正文"
          description="文章可能仍在切分处理中，稍后刷新即可。"
          action={
            <Button type="button" variant="outline" size="sm" onClick={() => void articleQuery.refetch()}>
              刷新
            </Button>
          }
        />
      ) : (
        <article ref={proseRef} className="reader-prose mx-auto mt-6 space-y-4">
          {article.paragraphs.map((paragraph) => (
            <p key={paragraph.paraIndex} className="whitespace-pre-wrap">
              {paragraph.sentences.map((sentence, index) => (
                <Fragment key={sentence.id}>
                  {index > 0 ? ' ' : null}
                  <SentenceText
                    sentenceId={sentence.id}
                    globalIndex={sentence.globalIndex}
                    text={sentence.text}
                    isLong={sentence.isLong}
                    highlightLong={highlightLong}
                    active={activeSentenceId === sentence.id}
                    analyzing={analyzingId === sentence.id}
                    onWordClick={handleWordClick}
                    onAnalyze={handleAnalyzeClick}
                  />
                </Fragment>
              ))}
            </p>
          ))}
        </article>
      )}

      {/* ⑥ 本文长难句清单（懒加载） */}
      {article.longSentenceCount > 0 && (
        <section className="mx-auto mt-10 max-w-3xl">
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-expanded={showLongList}
            onClick={() => setShowLongList((value) => !value)}
          >
            <ScanText aria-hidden className="size-4" />
            查看本文长难句（{article.longSentenceCount}）
            <ChevronDown aria-hidden className={cn('size-4 transition-transform', showLongList && 'rotate-180')} />
          </Button>

          {showLongList && (
            <div className="mt-3 space-y-2">
              {longQuery.isPending && <LoadingBlock label="正在取长难句…" />}
              {longQuery.isError && (
                <ErrorBlock message={toDisplayMessage(longQuery.error)} onRetry={() => void longQuery.refetch()} />
              )}
              {longQuery.data && longItems.length === 0 && (
                <EmptyBlock title="这篇没有长难句" description="按当前阈值判定，所有句子都在正常长度内。" />
              )}
              {longItems.map(({ sentence, score, reasons }) => (
                <div key={sentence.id} className="rounded-lg border bg-card px-3 py-2 text-card-foreground">
                  <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Badge tone="muted">第 {sentence.globalIndex + 1} 句</Badge>
                    <Badge tone="warning">难度 {Math.round(score)}</Badge>
                    {sentence.analyzed && <Badge tone="primary">已解析</Badge>}
                    <span>{sentence.tokenCount} 词</span>
                  </div>
                  <p className="mt-1.5 font-serif text-sm">{sentence.text}</p>
                  {reasons.length > 0 && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      判难依据：{reasons.map((reason) => REASON_LABELS[reason]).join('、')}
                    </p>
                  )}
                  <div className="mt-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      loading={analyzingId === sentence.id}
                      onClick={() => void runAnalyze(sentence.id, sentence.globalIndex)}
                    >
                      解析
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ⑦ 词卡与自评表单 */}
      <WordCard
        target={target}
        articleId={articleId}
        autoCollect={autoCollect}
        onClose={() => setTarget(null)}
      />

      <Dialog
        open={finishOpen}
        onClose={() => setFinishOpen(false)}
        title="这篇难不难？"
        className="sm:max-w-md"
      >
        <p className="text-sm text-muted-foreground">
          自评只影响给你的学习建议（推荐更合适的难度），不代表正确率。
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {SELF_RATINGS.map(({ value, label }) => (
            <Button
              key={value}
              type="button"
              variant="outline"
              size="sm"
              aria-label={`自评 ${value} 分：${label}`}
              loading={finishMutation.isPending && finishMutation.variables === value}
              onClick={() => finishMutation.mutate(value)}
            >
              {value} {label}
            </Button>
          ))}
        </div>
        {finishMutation.isError && (
          <p role="alert" className="mt-3 text-xs text-destructive">
            {toDisplayMessage(finishMutation.error)}
          </p>
        )}
      </Dialog>
    </ReaderFrame>
  );
}

// ---------------------------------------------------------------------------
// 上报节奏与滚动节流的常量（改这里就能整体调松/调紧）
// ---------------------------------------------------------------------------

/** 最长静默上报间隔（秒）。再短就是无谓请求，再长则"读到一半关页面"会丢较多进度。 */
const REPORT_INTERVAL_SECONDS = 30;
/** 或者跨过这么多句就报一次：快速滚动时不必等满 30 秒。 */
const REPORT_SENTENCE_DELTA = 20;
/** 进度条显示的刷新节流（毫秒）。 */
const DISPLAY_SYNC_THROTTLE_MS = 500;

/** 「这篇难不难」的 1–5 自评。给每档中文标签而不是只给数字：1–5 的分值方向很容易记反。 */
const SELF_RATINGS = [
  { value: 1, label: '很轻松' },
  { value: 2, label: '偏简单' },
  { value: 3, label: '一般' },
  { value: 4, label: '偏难' },
  { value: 5, label: '很吃力' },
] as const;

/**
 * 阅读页统一的外层容器：一级标题 + 窄内容 + 移动端留白。
 *
 * **`<h1>文章阅读</h1>` 刻意放在这里而不是数据分支里**，有三个原因：
 * 1. 三种状态（加载中 / 失败或 404 / 正常）共用同一个容器，所以任何一条分支下
 *    页面都有一个包含「文章阅读」的 h1 —— `e2e/app-shell.spec.ts` 会直接访问
 *    `/read/demo-article-id`（该 id 不存在，走 404 分支）并断言这一点；
 * 2. 它是"这个页面是什么"的说明，和文章数据无关，本就不该随请求成败出现或消失；
 * 3. 站点约定是 h1 = 页名（首页、个人中心都如此），文章标题因此降为 h2（见下面 header）。
 *
 * h1 视觉上做成小号的页面标签，是为了不与文章标题（h2，字号更大）抢视线：
 * 阅读时用户要看到的是文章名，不是"文章阅读"这四个字。
 */
function ReaderFrame({ children }: { children: React.ReactNode }) {
  return (
    <section className="mx-auto max-w-3xl px-4 py-6">
      <h1 className="mb-2 text-base font-semibold tracking-wide text-muted-foreground">文章阅读</h1>
      {children}
    </section>
  );
}

function BackToCatalog({ className }: { className?: string }) {
  return (
    <p className={className}>
      <Link to={ROUTES.textbooks} className="text-sm text-primary underline">
        返回教材目录
      </Link>
    </p>
  );
}
